import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  assessQuality,
  estimateGlareRatio,
  estimateSharpness,
} from '../lib/goodsInQuality';
import { stripViaCanvas } from './sanitize';
import { addPhoto, discardPhoto, QueueQuotaError } from './photoQueue';
import CapturePageList from './CapturePageList';

/**
 * Camera capture with a framing guide, multiple pages per document, and per-page
 * retake/delete. Each accepted page is quality-checked on a downscaled frame, then
 * sanitized (GPS stripped), then queued — in that order, so the hash the queue
 * records is the hash of the bytes that will be sent.
 *
 * getUserMedia is unavailable in jsdom and on some devices; the file input is the
 * fallback, not a degraded mode.
 *
 * Multi-page mode (`onSendPages` given; the Invoices screen's "Scan pages"): the pages stay on this screen instead of
 * going to the goods-in queue, can be put in order or retaken in their place, and "Send as one invoice" hands their
 * blobs, in order, to `onSendPages`. A refused send keeps the pages so the person can try again.
 *
 * `twoColumn` (the Invoices screen on a computer): a 4:3 camera about 420px wide with the page list and its actions
 * beside it, stacked on a phone, and "Add page" once there is a page. Other callers keep the full-width stage.
 */
export const ANALYSIS_MAX_EDGE = 320;

/** The retake copy shown for each reason the shared gate returns. */
export const REASON_MESSAGE = Object.freeze({
  resolution_too_low: 'Retake: too small',
  blur: 'Retake: too blurry',
  glare: 'Retake: glare',
});

/**
 * Downscale a frame and return its ImageData plus the frame's true pixel size.
 * The resolution rule is judged on the real capture size, not the downscaled one.
 */
export function frameToImageData(source, maxEdge = ANALYSIS_MAX_EDGE) {
  const sw = source.videoWidth || source.naturalWidth || source.width;
  const sh = source.videoHeight || source.naturalHeight || source.height;
  if (!sw || !sh) return null;
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, w, h);
  return { imageData: ctx.getImageData(0, 0, w, h), fullWidth: sw, fullHeight: sh };
}

/**
 * Blur and glare check for one frame, using the shared thresholds in
 * src/lib/goodsInQuality.js (which mirrors backend/receiving/goods_in_capture.py).
 * The server re-runs its own gate on upload, so this is a fast pre-check.
 */
export function checkFrame(source, maxEdge = ANALYSIS_MAX_EDGE) {
  const scaled = frameToImageData(source, maxEdge);
  if (!scaled) {
    return { accepted: false, reasons: ['resolution_too_low'], message: 'Retake: too small', metrics: null };
  }
  const { imageData, fullWidth, fullHeight } = scaled;
  const verdict = assessQuality({
    width: fullWidth,
    height: fullHeight,
    sharpness: estimateSharpness(imageData),
    glareRatio: estimateGlareRatio(imageData),
  });
  return { ...verdict, message: verdict.accepted ? null : REASON_MESSAGE[verdict.reasons[0]] };
}

export default function CameraCapture({
  captureType = 'delivery_note',
  receiptId = null,
  organizationId = null,
  locationId = null,
  userId = null,
  onQueued = null,
  onDiscarded = null,
  isCurrent = () => true,
  apiUrl = '',
  onSendPages = null,
  twoColumn = false,
}) {
  const multiPage = typeof onSendPages === 'function';
  const seq = useRef(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const hasGetUserMedia = typeof navigator !== 'undefined'
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === 'function';

  // Start in the correct state: a browser with no camera API shows the fallback
  // immediately rather than an empty stage and a dead button.
  const [cameraState, setCameraState] = useState(hasGetUserMedia ? 'idle' : 'unsupported');
  const [pages, setPages] = useState([]);
  const [rejection, setRejection] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retakeId, setRetakeId] = useState(null);

  const stopStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const startCamera = useCallback(async () => {
    if (!hasGetUserMedia) {
      setCameraState('unsupported');
      return;
    }
    setCameraState('starting');
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play?.();
      }
      setCameraState('live');
    } catch (err) {
      // Permission refused, no camera, or an insecure context. Fall back.
      setCameraState('refused');
      setError('Camera unavailable. Use the file button to add a photo.');
    }
  }, [hasGetUserMedia]);

  /** Sanitize, hash and queue one accepted frame. */
  const acceptFrame = useCallback(async (blob) => {
    setError(null);
    if (multiPage) {
      seq.current += 1;
      const page = { id: `page-${seq.current}`, url: URL.createObjectURL(blob), blob };
      setPages((prev) => {
        const at = retakeId ? prev.findIndex((p) => p.id === retakeId) : -1;
        if (at < 0) return [...prev, page];
        URL.revokeObjectURL(prev[at].url);
        return [...prev.slice(0, at), page, ...prev.slice(at + 1)];
      });
      setRetakeId(null);
      return;
    }
    setBusy(true);
    try {
      if (!isCurrent()) throw new Error('Session or site changed. Reopen capture before saving.');
      const row = await addPhoto({
        blob,
        organization_id: organizationId,
        location_id: locationId,
        user_id: userId,
        capture_type: captureType,
        receipt_id: receiptId,
      });
      if (!isCurrent()) return;
      setPages((prev) => [...prev, { id: row.id, url: URL.createObjectURL(blob), sha256: row.sha256 }]);
      if (onQueued) onQueued(row);
    } catch (err) {
      if (err instanceof QueueQuotaError) setError(err.message);
      else setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }, [captureType, locationId, onQueued, organizationId, receiptId, userId, isCurrent, multiPage, retakeId]);

  /** Run the quality gate on a source, then sanitize and queue. */
  const gateAndQueue = useCallback(async (source) => {
    setRejection(null);
    const verdict = checkFrame(source);
    if (!verdict.accepted) {
      setRejection(verdict.message || 'Retake');
      return;
    }
    const sanitized = await stripViaCanvas(source);
    await acceptFrame(sanitized);
  }, [acceptFrame]);

  const takePhoto = useCallback(async () => {
    if (!videoRef.current) return;
    await gateAndQueue(videoRef.current);
  }, [gateAndQueue]);

  const onFilePicked = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('could not read that image'));
        el.src = url;
      });
      await gateAndQueue(img);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      URL.revokeObjectURL(url);
      event.target.value = '';
    }
  }, [gateAndQueue]);

  const removePage = useCallback(async (id) => {
    if (multiPage) {
      setPages((prev) => {
        const page = prev.find((p) => p.id === id);
        if (page) URL.revokeObjectURL(page.url);
        return prev.filter((p) => p.id !== id);
      });
      setRetakeId((r) => (r === id ? null : r));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (!isCurrent()) throw new Error('Session or site changed. Reopen capture.');
      await discardPhoto(id, { userId, orgId: organizationId, locationId });
      // No optimistic removal: keep the preview on failure, including an upload race.
      setPages((prev) => {
        const page = prev.find((p) => p.id === id);
        if (page) URL.revokeObjectURL(page.url);
        return prev.filter((p) => p.id !== id);
      });
      if (onDiscarded) await onDiscarded(id);
    } catch (err) {
      setError(err.message || String(err));
    } finally { setBusy(false); }
  }, [userId, organizationId, locationId, onDiscarded, isCurrent, multiPage]);

  const movePage = useCallback((id, step) => {
    setPages((prev) => {
      const from = prev.findIndex((p) => p.id === id);
      const to = from + step;
      if (from < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  }, []);

  const sendPages = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await onSendPages(pages.map((p) => p.blob));
      pages.forEach((p) => URL.revokeObjectURL(p.url));
      setPages([]);
    } catch (err) {
      setError(err.message || String(err));   // the pages stay, to send again
    } finally {
      setBusy(false);
    }
  }, [onSendPages, pages]);

  // Pages kept on this screen hold their photos in memory; leaving (Cancel, or the screen closing) lets them go.
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  useEffect(() => () => {
    if (multiPage) pagesRef.current.forEach((p) => URL.revokeObjectURL(p.url));
  }, [multiPage]);

  const retakeIndex = pages.findIndex((p) => p.id === retakeId);

  const camera = (
    <>
      <div className="relative bg-black rounded-lg overflow-hidden" data-testid="camera-stage">
        <video
          ref={videoRef}
          playsInline
          muted
          data-testid="camera-video"
          className={`w-full object-cover ${twoColumn ? 'aspect-[4/3]' : 'h-[320px]'}`}
        />
        {/* Framing guide */}
        <div className="pointer-events-none absolute inset-6 border-2 border-white/70 rounded" data-testid="framing-guide" />
      </div>

      {cameraState === 'unsupported' && (
        <p className="text-sm text-ink-muted" data-testid="camera-unsupported">
          This browser has no camera access. Use the file button below.
        </p>
      )}
      {cameraState === 'refused' && (
        <p className="text-sm text-ink-muted" data-testid="camera-refused">
          Camera permission was refused. Use the file button below.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="bg-primary-500 text-ink-inverse px-4 py-2 min-h-[44px] rounded disabled:opacity-40"
          data-testid="take-photo"
          onClick={takePhoto}
          disabled={busy || cameraState !== 'live'}
        >
          {twoColumn && pages.length > 0 && retakeIndex < 0 ? 'Add page' : 'Take photo'}
        </button>
        {cameraState !== 'live' && hasGetUserMedia && (
          <button
            type="button"
            className="border border-surface-border px-4 py-2 min-h-[44px] rounded"
            data-testid="start-camera"
            onClick={startCamera}
          >
            Start camera
          </button>
        )}
        {/* Fallback, always available. */}
        <label className="border border-surface-border px-4 py-2 min-h-[44px] rounded inline-flex items-center cursor-pointer">
          <span className="text-sm">Use a file</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            data-testid="camera-file-input"
            disabled={busy}
            className="hidden"
            onChange={onFilePicked}
          />
        </label>
      </div>

      {rejection && (
        <p className="text-sm text-danger" role="alert" data-testid="camera-rejection">
          {rejection}
        </p>
      )}
      {error && (
        <p className="text-sm text-danger" role="alert" data-testid="camera-error">{error}</p>
      )}
    </>
  );

  const pageList = (
    <>
      {twoColumn && !pages.length && (
        <p className="text-sm text-ink-muted" data-testid="capture-pages-empty">The pages you take appear here.</p>
      )}
      {retakeIndex >= 0 && (
        <p className="text-sm text-ink flex flex-wrap items-center gap-2" role="status" data-testid="retake-banner">
          Retaking page {retakeIndex + 1}: take or choose that page again.
          <button type="button" className="underline min-h-[44px]" data-testid="retake-cancel" onClick={() => setRetakeId(null)}>
            Keep the old one
          </button>
        </p>
      )}

      <CapturePageList
        pages={pages}
        busy={busy}
        multiPage={multiPage}
        onRetake={multiPage ? setRetakeId : removePage}
        onDelete={removePage}
        onMove={movePage}
      />

      {multiPage && (
        <button
          type="button"
          className="w-full sm:w-auto bg-primary-500 text-ink-inverse px-5 py-3 min-h-[52px] rounded-xl font-medium disabled:opacity-40"
          data-testid="send-pages"
          disabled={busy || !pages.length || retakeIndex >= 0}
          onClick={sendPages}
        >
          {busy ? 'Sending…' : `Send as one invoice (${pages.length} page${pages.length === 1 ? '' : 's'})`}
        </button>
      )}
    </>
  );

  if (twoColumn) {
    return (
      <div className="grid gap-4 md:grid-cols-[420px_minmax(0,1fr)] md:items-start" data-testid="camera-capture">
        <div className="min-w-0 space-y-3" data-testid="camera-column">{camera}</div>
        <div className="min-w-0 space-y-3" data-testid="camera-pages-column">{pageList}</div>
      </div>
    );
  }
  return <div className="space-y-3" data-testid="camera-capture">{camera}{pageList}</div>;
}
